import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import { IngredientIssueOutcome, type IngredientCorrection } from "@fitness-app/data-models/entities/DishIssue";
import { type Json } from "@fitness-app/supabase";

import { getTraineeIssueKind, type DishIssueListItem } from "~/modules/Nutrition/DishIssues/ingredientIssues";
import { analyticsService, supabase } from "~/store/initializeStore";

/** What the on_dish_issue_update webhook reads to rewrite an edited dish in the meals plan templates. */
export interface TraineeDishIssueResolution {
  updateTemplates: boolean;
  flow: string;
  beforeUpdate: unknown;
}

export interface ResolveTraineeIssuePayload {
  issue: DishIssueListItem;
  outcome: IngredientIssueOutcome;
  /** Product reports only: fields to write; empty or missing closes the report without touching the product. */
  changes?: IngredientCorrection | null;
  /** Dish reports only, with the `resolved` outcome: set after the dish was saved in the dish form. */
  metadata?: TraineeDishIssueResolution | null;
}

const SUCCESS_MESSAGE: Record<IngredientIssueOutcome, string> = {
  [IngredientIssueOutcome.Resolved]: "Zgłoszenie zostało rozwiązane",
  [IngredientIssueOutcome.Rejected]: "Zgłoszenie zostało odrzucone",
};

/**
 * TRE-2414 - a client's product or dish report is decided only through `resolve_trainee_issue`: staff have
 * no UPDATE on these rows, and for products the RPC applies the changes the trainer has no right to write
 * directly. A dish is saved through the dish form first; the RPC only closes the report.
 */
export const useResolveTraineeIssue = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    async mutationFn({ issue, outcome, changes, metadata }: ResolveTraineeIssuePayload) {
      const withChanges = Boolean(changes && Object.keys(changes).length);
      const { error } = await supabase.rpc("resolve_trainee_issue", {
        p_issue_id: issue.id,
        p_outcome: outcome,
        p_changes: withChanges ? (changes as unknown as Json) : null,
        p_metadata: metadata ? (metadata as unknown as Json) : null,
      });

      if (error) {
        throw new Error(error.message);
      }

      return { withChanges: withChanges || Boolean(metadata?.updateTemplates) };
    },
    onSuccess: async ({ withChanges }, { issue, outcome }) => {
      analyticsService.track("ingredient_issue_resolved", {
        kind: getTraineeIssueKind(issue),
        outcome,
        categories: issue.categories,
        withChanges,
        traineeId: issue.traineeId,
      });
      message.success(SUCCESS_MESSAGE[outcome]);
      // The list and the tab badge share this key prefix.
      await queryClient.invalidateQueries({ queryKey: ["dishIssues"] });
    },
    onError: (error: Error) => {
      message.error(error.message);
    },
  });

  return {
    resolveIssue: mutation.mutateAsync,
    isResolving: mutation.isLoading,
  };
};
